import { useFileUploader } from "../hooks/useFileUploader.js";
import { FileTypeButtons } from "../components/FileTypeButtons/index.js";
import { openFilePicker } from "../utils/open-file-picker.js";
import { FileUploadCard } from "../components/FileUploadCard/index.js";
import { CameraCapture } from "../components/CameraCapture/index.js";
import { ExistingUploadCard } from "../components/FileUploadCard/ExistingUploadCard.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { Label, Modal } from "@therms/atalaya";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { castArray, difference, uniq } from "lodash-es";
//#region src/modules/Files/shared/FilesPickerInput.tsx
var FilesPickerInput = ({ allowedTypes, autoUpload = true, hint, label, multi = true, onChangeValue, onSetPrimaryImage, onUploadsComplete, previouslyUploadedFileUrls, primaryImageUrl, setUploadInProgress, subText }) => {
	const local = useRef({ lastOnChangeValues: [] });
	const [loadingAssets, setLoadingAssets] = useState(true);
	const [showCapture, setShowCapture] = useState(false);
	const [existingUploadedFileUrls, setExistingUploadedFileUrls] = useState(castArray(previouslyUploadedFileUrls));
	useEffect(() => {
		const SCRIPT_ID = "image-blob-reduce";
		if (!document.getElementById(SCRIPT_ID)) {
			const script = document.createElement("script");
			script.setAttribute("src", "https://unpkg.com/image-blob-reduce@4.1.0/dist/image-blob-reduce.min.js");
			script.setAttribute("id", SCRIPT_ID);
			script.setAttribute("type", "text/javascript");
			script.onload = () => {
				setLoadingAssets(false);
			};
			script.onerror = (err) => {
				console.error("There was an error loading FilesPickerInput assets", err);
			};
			document.body.appendChild(script);
		}
	}, []);
	const types = useMemo(() => {
		if (!allowedTypes) return [
			"imagePicker",
			"filePicker",
			"videoCapture"
		];
		return allowedTypes;
	}, [allowedTypes]);
	const canAddFile = (file, allFiles) => {
		return true;
	};
	const filePreprocessor = (file) => {
		const ImageBlockReduce = window["ImageBlobReduce"];
		if (file.type.includes("image") && ImageBlockReduce) return ImageBlockReduce().toBlob(file.file, { max: 1e3 }).then((blob) => {
			file.file = new File([blob], file.name, {
				lastModified: file.file.lastModified,
				type: file.file.type
			});
			file.size = file.file.size;
			file.type = file.file.type;
			return file;
		});
		return Promise.resolve(file);
	};
	const { addFiles, allUploadsComplete, files, removeAllFiles, removeFile, replaceAllFiles, retryFileUpload, uploadInProgress, uploadProgressStatus } = useFileUploader({
		canAddFile,
		filePreprocessor,
		immediateUpload: autoUpload
	});
	useEffect(() => {
		if (setUploadInProgress) setUploadInProgress(!allUploadsComplete);
		if (onUploadsComplete) onUploadsComplete(allUploadsComplete);
	}, [allUploadsComplete]);
	useEffect(() => {
		const t = setTimeout(() => {
			if (onChangeValue) {
				const allFileUrls = uniq(files.filter(({ accessUrl, status }) => status === "uploaded" && accessUrl).map(({ accessUrl }) => accessUrl).concat(...existingUploadedFileUrls || [])).filter((x) => !!x);
				if (JSON.stringify(local.current.lastOnChangeValues) !== JSON.stringify(allFileUrls)) {
					local.current.lastOnChangeValues = allFileUrls;
					onChangeValue(allFileUrls);
				}
			}
		}, 100);
		return () => clearTimeout(t);
	}, [existingUploadedFileUrls, files]);
	useEffect(() => {
		const _previouslyUploadedFileUrls = castArray(previouslyUploadedFileUrls);
		if (_previouslyUploadedFileUrls && _previouslyUploadedFileUrls !== existingUploadedFileUrls) {
			const fileUrls = files.map(({ accessUrl }) => accessUrl).filter((x) => !!x);
			const urlsToAddToExisting = difference(_previouslyUploadedFileUrls, [...existingUploadedFileUrls, ...fileUrls]);
			if (urlsToAddToExisting.length) setExistingUploadedFileUrls([...existingUploadedFileUrls, ...urlsToAddToExisting]);
		}
	}, [previouslyUploadedFileUrls]);
	const handlePickFileType = (fileType) => {
		if (fileType === "capture") {
			setShowCapture(true);
			return;
		}
		let allowedFilePattern = "*";
		switch (fileType) {
			case "audio":
				allowedFilePattern = "audio/*";
				break;
			case "capture":
			case "image":
				allowedFilePattern = "image/*";
				break;
			case "video":
				allowedFilePattern = "video/*";
				break;
			default: allowedFilePattern = "*";
		}
		const onPickedFiles = (fileList) => {
			if (!multi) setExistingUploadedFileUrls([]);
			(multi ? addFiles : replaceAllFiles)(fileList);
		};
		openFilePicker(onPickedFiles, {
			accept: allowedFilePattern,
			capture: fileType === "capture" ? "camera" : void 0,
			multiple: multi
		});
	};
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsxs("div", { children: [
		/* @__PURE__ */ jsxs("div", {
			className: "flex justify-between",
			children: [label && /* @__PURE__ */ jsx("div", {
				className: "mb-base",
				children: /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(Label, { children: label }) })
			}), hint && /* @__PURE__ */ jsx("div", {
				className: "mb-base",
				children: /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(Label, { children: hint }) })
			})]
		}),
		/* @__PURE__ */ jsx(FileTypeButtons, {
			disabled: loadingAssets,
			noAudio: true,
			noFile: !types?.includes("filePicker"),
			noCapture: !types?.includes("cameraCapture"),
			noImage: !types?.includes("imagePicker"),
			noVideo: !types?.includes("videoCapture"),
			onFileTypeClick: handlePickFileType
		}),
		uploadInProgress && /* @__PURE__ */ jsxs("div", {
			className: "my-sm",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "mb-xxs flex justify-between",
				children: [/* @__PURE__ */ jsx("span", {
					className: "text-color-main font-bold",
					children: "Uploading"
				}), /* @__PURE__ */ jsxs("span", {
					className: "text-color-main text-sm font-bold",
					children: [uploadProgressStatus, "%"]
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "h-3 w-full rounded-full bg-gray-200",
				children: /* @__PURE__ */ jsx("div", {
					className: "bg-main-dark h-3 rounded-full",
					style: { width: `${uploadProgressStatus.toString()}%` }
				})
			})]
		}),
		subText && /* @__PURE__ */ jsx("div", {
			className: "my-sm text-color-subtle",
			children: subText
		}),
		Array.isArray(existingUploadedFileUrls) && /* @__PURE__ */ jsx("div", {
			className: "gap-xs mt-xs flex flex-wrap",
			children: existingUploadedFileUrls.map((url) => /* @__PURE__ */ jsx(ExistingUploadCard, {
				isPrimaryImage: primaryImageUrl === url,
				onSetPrimaryImage,
				onRemove: () => {
					setExistingUploadedFileUrls(existingUploadedFileUrls.filter((_url) => _url !== url));
				},
				url
			}, url))
		}),
		/* @__PURE__ */ jsx("div", {
			className: "gap-xs mt-xs flex flex-wrap",
			children: files.map((file) => {
				return /* @__PURE__ */ jsx(FileUploadCard, {
					isPrimaryImage: primaryImageUrl === file.accessUrl,
					retryFileUpload,
					onSetPrimaryImage,
					file,
					onCancel: () => removeFile(file.id)
				}, file.id);
			})
		})
	] }), showCapture && /* @__PURE__ */ jsx(Modal, {
		closeHandler: () => setShowCapture(false),
		header: "Photo",
		width: "xxl",
		children: /* @__PURE__ */ jsx(CameraCapture, { onCapture: (file) => {
			if (multi) addFiles([file]);
			else {
				setExistingUploadedFileUrls([]);
				removeAllFiles();
				addFiles([file]);
			}
			setShowCapture(false);
		} })
	})] });
};
//#endregion
export { FilesPickerInput };

//# sourceMappingURL=FilesPickerInput.js.map